import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { FiActivity, FiDollarSign, FiTrendingUp } from 'react-icons/fi';
import { api, normalizeToken } from '../helpers';
import { API_BASE_URL } from '../helpers/config';
import { pageTransition, staggerContainer, staggerItem } from '../helpers/animations';
import { DashboardSkeleton, ReportListSkeleton } from '../components/skeletons';

const LIVE_TEMPLATES = [
  (id, inr, usdt) =>
    `A transaction has been completed where User ID ${id} converted ₹${inr} into ${usdt} USDT.`,
  (id, inr, usdt) =>
    `User ID ${id} completed an exchange of ₹${inr}, receiving ${usdt} USDT in return.`,
  (id, inr, usdt) =>
    `₹${inr} has been successfully converted to ${usdt} USDT for User ID ${id}.`,
  (id, inr, usdt) =>
    `User ID ${id} initiated and completed an exchange of ₹${inr} to ${usdt} USDT.`,
  (id, inr, usdt) =>
    `A successful exchange transaction has been recorded: User ID ${id} exchanged ₹${inr} for ${usdt} USDT.`,
  (id, inr, usdt) =>
    `User ID ${id} has processed an exchange request, converting ₹${inr} into ${usdt} USDT.`,
  (id, inr, usdt) =>
    `Exchange completed: ₹${inr} has been credited as ${usdt} USDT to User ID ${id}.`,
  (id, inr, usdt) =>
    `User ID ${id} has finalized an exchange transaction of ₹${inr} equivalent to ${usdt} USDT.`,
  (id, inr, usdt) =>
    `The system confirms that User ID ${id} exchanged ₹${inr} and received ${usdt} USDT successfully.`,
];

function Dashboard() {
  const { token: tokenParam } = useParams();
  const token = normalizeToken(tokenParam);
  const [stats, setStats] = useState({ usdt: 0, inr: 0 });
  const [loadingStats, setLoadingStats] = useState(true);

  const [liveAll, setLiveAll] = useState([]);
  const [liveLoading, setLiveLoading] = useState(true);

  const trackRef = useRef(null);
  const [halfHeight, setHalfHeight] = useState(0);

  useEffect(() => {
    const fetchStats = async () => {
      if (!token) {
        toast.error('Session expired. Please open your access link again.');
        return;
      }

      setLoadingStats(true);
      try {
        const { data } = await api.post(`${API_BASE_URL}/exchange/statistics`, { token });
        if (data?.error === false && data?.data) {
          setStats({
            usdt: Number(data.data.usdt || 0),
            inr: Number(data.data.inr || 0),
          });
        } else if (data?.error === 'session expired') {
          toast.error('Session expired. Please open your access link again.');
        } else {
          toast.error(data?.error || 'Failed to fetch statistics');
        }
      } catch {
        // api helper shows toast
      } finally {
        setLoadingStats(false);
      }
    };

    const fetchLive = async () => {
      if (!token) return;

      setLiveLoading(true);
      try {
        const { data } = await api.post(`${API_BASE_URL}/exchange/live-exchanges`, { token });
        if (data?.error === false && Array.isArray(data?.data)) {
          const all = data.data.map((item, index) => {
            const inr = Number(item.amount || 0).toFixed(2);
            const usdt = Number(item.usdt || 0).toFixed(2);
            const tmplIndex = Math.floor(Math.random() * LIVE_TEMPLATES.length);
            const message = LIVE_TEMPLATES[tmplIndex](item.user_id, inr, usdt);
            return {
              ...item,
              message,
              _key: `${item.user_id}-${inr}-${usdt}-${tmplIndex}-${index}`,
            };
          });
          setLiveAll(all);
        } else if (data?.error && data?.error !== 'session expired') {
          toast.error(data.error);
        }
      } catch {
        // api helper shows toast
      } finally {
        setLiveLoading(false);
      }
    };

    fetchStats();
    fetchLive();
  }, [token]);

  const feedItems = liveAll.length > 0 ? [...liveAll, ...liveAll] : [];

  useLayoutEffect(() => {
    if (!trackRef.current) return;
    const el = trackRef.current;

    const measure = () => {
      const h = el.scrollHeight / 2;
      if (Number.isFinite(h) && h > 0) setHalfHeight(h);
    };

    const raf = window.requestAnimationFrame(measure);
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);

    return () => {
      window.cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [feedItems.length]);

  return (
    <motion.div
      {...pageTransition}
      transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="dashboard-page"
      style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
    >
      {loadingStats && liveLoading ? (
        <DashboardSkeleton />
      ) : (
        <>
      <motion.div className="dashboard-hero" variants={staggerItem} initial="initial" animate="animate">
        <h1 className="screen-title" style={{ margin: 0 }}>
          Home
        </h1>
        <p className="dashboard-subtitle">Live exchange activity and your totals</p>
      </motion.div>

      <motion.div
        className="stats-row dashboard-stats"
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
        <motion.div className="stat-card" variants={staggerItem}>
          <div className="stat-head">
            <span className="stat-icon stat-icon-green" aria-hidden="true">
              <FiDollarSign size={16} />
            </span>
            <span className="stat-label">Total USDT exchanged</span>
          </div>
          <span className="stat-value">{loadingStats ? '—' : stats.usdt.toFixed(2)}</span>
        </motion.div>
        <motion.div className="stat-card" variants={staggerItem}>
          <div className="stat-head">
            <span className="stat-icon stat-icon-blue" aria-hidden="true">
              <FiTrendingUp size={16} />
            </span>
            <span className="stat-label">Total INR volume</span>
          </div>
          <span className="stat-value">{loadingStats ? '—' : `₹${stats.inr.toFixed(2)}`}</span>
        </motion.div>
      </motion.div>

      <motion.div
        className="card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.25 }}
      >
        <div className="card-title-row">
          <h2 className="card-title">Live exchanges</h2>
          <span className="card-chip">
            <FiActivity size={14} /> Live feed
          </span>
        </div>

        {liveLoading && <ReportListSkeleton rows={5} />}

        {!liveLoading && liveAll.length === 0 && (
          <p className="form-hint" style={{ margin: 0 }}>
            No recent exchanges.
          </p>
        )}

        {!liveLoading && liveAll.length > 0 && (
          <div className="live-feed-viewport">
            <motion.div
              key={halfHeight || 'loading'}
              ref={trackRef}
              className="live-feed-track"
              initial={halfHeight ? { y: -halfHeight } : { y: 0 }}
              animate={halfHeight ? { y: 0 } : { y: 0 }}
              transition={
                halfHeight
                  ? {
                    duration: Math.max(12, halfHeight / 28),
                    ease: 'linear',
                    repeat: Infinity,
                  }
                  : undefined
              }
            >
              <ul className="exchange-list live-feed-list">
                {feedItems.map((item, idx) => (
                  <li key={`${item._key}-${idx}`} className="exchange-item live-feed-item">
                    <span className="exchange-item-text">{item.message}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        )}

      </motion.div>
        </>
      )}
    </motion.div>
  );
}

export default Dashboard;

